/** Slow decorative loops run on the scene's clock (v1.185.0).
 *
 *  An infinite CSS animation of `transform` or `opacity` runs on the
 *  compositor at the DISPLAY's rate: 60 frames a second, 120 or 144 on a fast
 *  screen, each one a new frame for the whole page. Under the frosted glass a
 *  new frame is not a small thing: every pane re-blurs what is behind it. And
 *  the theme scene already draws its own frames at the decorative rate
 *  (`targetFps('decorative')`, 30 at most), on a different phase, so a page
 *  with one pulsing ring drew the scene's frames AND the ring's in between.
 *  Measured on 2026-09-27 in headless Chrome on this machine's GPU (D3D11),
 *  idle, 1366x800, CPU in the renderer and GPU processes: the Read Aloud
 *  cube's pulse cost MORE than the whole animated background (Read idle ~430ms
 *  a second, ~210 with the pulse still, ~53 with everything still), About's
 *  ten loops took it to ~570, Community's floating emoji to ~500.
 *
 *  So every SLOW loop (a period of at least `MIN_PERIOD_MS`: the cube's pulse,
 *  the Sounds button's breathing, the streak flame, About's rays, an empty
 *  state's float) is paused and stepped from the shared frame driver at the
 *  decorative rate, in the scene's phase (`onFrame` gives a consumer at the
 *  scene's rate the scene's phase). Its keyframes, easing, period and phase
 *  are the browser's own, untouched: only how often the value is sampled
 *  changes, the same trade the scene and the rim light (`ui/motion.ts`)
 *  already make, and it lands in frames the glass re-blurs anyway. Measured
 *  the same way, old and new builds interleaved: Read ~390 -> ~203ms a second
 *  with the pulse still pulsing, Study ~370 -> ~203, About ~590 -> ~203,
 *  Community ~500 -> ~170. The cost of stepping them here is a style recalc
 *  of the few animated elements per frame (~2.6ms a second).
 *
 *  What stays native: anything faster (a spinner, an indeterminate progress
 *  bar, a busy dot: short, and stepping them would show), anything finite,
 *  and everything under Reduced Motion (its clamp makes every duration
 *  0.01ms, far under the floor). A loop inside a tab that is not on screen is
 *  not stepped (display:none would not have run it; `keepAlive` restarts it
 *  from its first frame on the way back, and the next step adopts that). With
 *  no slow loop on the page the driver is not joined at all, and it stops with
 *  the page hidden, as every consumer does; a loop then resumes at the phase
 *  its native clock would have reached, exactly as a CSS animation does.
 *
 *  Anything this cannot see stays native, never broken: a browser without
 *  `CSSAnimation` or `getAnimations` gives nothing to adopt. */
import { onFrame, targetFps } from './power';

/** The shortest period stepped at the decorative rate. The ambient loops are
 *  1.6-120s; the spinners and progress sweeps they must not touch are 0.8-1.4s. */
export const MIN_PERIOD_MS = 1500;

interface Loop {
  /** `now - currentTime`: where the loop's own clock started. */
  origin: number;
  /** The tab panel it lives in, or null outside every panel. */
  panel: HTMLElement | null;
}

const loops = new Map<CSSAnimation, Loop>();
let stopDriver: (() => void) | null = null;

/** Is `a` a slow infinite loop this clock should step? */
export function isSlowLoop(iterations: number | undefined, duration: unknown): boolean {
  return iterations === Infinity && typeof duration === 'number' && duration >= MIN_PERIOD_MS;
}

function targetOf(a: Animation): Element | null {
  return (a.effect as KeyframeEffect | null)?.target ?? null;
}

function adopt(a: CSSAnimation): void {
  if (loops.has(a) || a.playState !== 'running') return;
  const timing = a.effect?.getComputedTiming();
  if (!timing || !isSlowLoop(timing.iterations, timing.duration)) return;
  const target = targetOf(a);
  if (!target) return;
  const now = performance.now();
  loops.set(a, { origin: now - (Number(a.currentTime) || 0), panel: target.closest<HTMLElement>('.tab-panel') });
  a.pause();
  if (!stopDriver) stopDriver = onFrame(step, { fps: () => targetFps('decorative') });
}

function step(_dt: number, now: number): void {
  for (const [a, loop] of loops) {
    if (a.playState === 'idle' || !targetOf(a)?.isConnected) { loops.delete(a); continue; }
    // Played again by someone else (keepAlive restarting a returning tab's
    // loops from their first frame): take it back from where it now stands.
    if (a.playState === 'running') { loop.origin = now - (Number(a.currentTime) || 0); a.pause(); continue; }
    if (loop.panel && !loop.panel.classList.contains('visible')) continue;
    a.currentTime = now - loop.origin;
  }
  if (!loops.size && stopDriver) { stopDriver(); stopDriver = null; }
}

/** Take every slow loop that starts from now on, and any already running.
 *  Idempotent; `main.ts` calls it once, right after `initPower()`. */
let installed = false;
export function installLoopClock(): void {
  if (installed || typeof document === 'undefined' || typeof CSSAnimation === 'undefined') return;
  if (typeof document.getAnimations !== 'function') return;
  installed = true;
  document.addEventListener('animationstart', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const pseudo = e.pseudoElement || null;
    for (const a of t.getAnimations({ subtree: !!pseudo })) {
      if (!(a instanceof CSSAnimation) || a.animationName !== e.animationName) continue;
      if (((a.effect as KeyframeEffect | null)?.pseudoElement ?? null) !== pseudo) continue;
      adopt(a);
    }
  }, true);
  for (const a of document.getAnimations()) if (a instanceof CSSAnimation) adopt(a);
}
